<template>
  <div
    ref="websiteSearchApp"
    class="w-full flex flex-col relative">
    <div
      v-if="state.searchInputType === SIDEBAR_SEARCH_INPUT_TYPE"
      class="w-full flex flex-col">
      <div class="w-full h-8 relative">
        <input
          ref="searchInput"
          v-model="state.searchValue"
          type="search"
          name="q"
          :autocomplete="state.isPartNumberSearch ? 'off' : undefined"
          :placeholder="state.isPartNumberSearch ? 'Enter part number' : 'Smart Search'"
          class="sidebar-search-input w-full h-8 border border-black py-2 pr-16 pl-5 [&::-webkit-search-cancel-button]:appearance-none text-sm placeholder:text-sm text-light-gray placeholder:text-light-gray"
          :style="sidebarSearchInputStyle"
          @input="searchInputHandler"
          @focus="partNumberSearchFocusHandler"
          @click="partNumberSearchFocusHandler"
          @keyup.enter="searchRequestHandler" />
        <button
          v-if="state.searchValue.length > 0"
          type="button"
          aria-label="Clear search"
          class="w-7 h-7 border-none bg-transparent p-0 absolute top-1/2 -translate-y-1/2 right-9 flex items-center justify-center text-gray hover:text-base-color cursor-pointer tr-all"
          @click="clearSearchInput">
          <svg
            class="w-4 h-4"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2.2"
            stroke-linecap="round"
            aria-hidden="true">
            <path d="M6 6l12 12M18 6L6 18" />
          </svg>
        </button>
        <div
          class="w-7 h-7 rounded-full bg-base-color hover:bg-dark-base-color border-none absolute top-1/2 -translate-y-1/2 right-0.5 cursor-pointer flex items-center justify-center tr-all"
          @click="searchRequestHandler">
          <svg
            class="block w-4 h-4 text-white"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2.4"
            stroke-linecap="round"
            aria-label="Search icon"
            role="img">
            <circle cx="10.5" cy="10.5" r="6.5" />
            <path d="M15.5 15.5L21 21" />
          </svg>
        </div>
      </div>
      <div
        v-if="isPartSearchEnabled"
        class="w-full flex items-start justify-end mt-1">
        <TrueFalseLabelsSwitcherComponent
          v-model:value="state.isPartNumberSearch"
          falseLabel="Smart Search"
          trueLabel="Part Search"
          textColor="text-gray" />
      </div>
    </div>

    <div
      v-if="state.searchInputType === PAGE_CONTENT_SEARCH_INPUT_TYPE"
      class="product-search-v1 w-full max-w-md flex flex-col">
      <div class="w-full flex flex-col items-end relative overflow-hidden bg-white rounded-[20px]">
        <input
          ref="searchInput"
          v-model="state.searchValue"
          type="text"
          name="Smart search"
          :autocomplete="state.isPartNumberSearch ? 'off' : undefined"
          class="page-content-search-input w-full h-10 bg-white border-none outline-none pl-5 pr-28 text-gray italic font-medium leading-normal"
          :placeholder="state.isPartNumberSearch ? 'Enter part number' : 'Smart search'"
          @input="searchInputHandler"
          @focus="partNumberSearchFocusHandler"
          @click="partNumberSearchFocusHandler"
          @keyup.enter="searchRequestHandler" />
        <div
          class="w-24 h-8 rounded-full absolute right-1 top-1 flex items-center justify-center cursor-pointer bg-base-color hover:bg-dark-base-color tr-all"
          @click="searchRequestHandler">
          <svg
            class="block w-4 h-4 mr-2 text-white"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2.4"
            stroke-linecap="round"
            aria-label="Search icon"
            role="img">
            <circle cx="10.5" cy="10.5" r="6.5" />
            <path d="M15.5 15.5L21 21" />
          </svg>
          <span class="font-medium text-white">Search</span>
        </div>
        <div
          v-if="isPartSearchEnabled"
          class="w-full flex justify-end px-4 py-1">
          <TrueFalseLabelsSwitcherComponent
            v-model:value="state.isPartNumberSearch"
            falseLabel="Smart Search"
            trueLabel="Part Search"
            textColor="text-gray" />
        </div>
      </div>
    </div>

    <Teleport to="body">
      <div
        v-if="isPartNumberSearchResultsVisible"
        class="bg-white border border-light-gray rounded-t-none rounded-b-lg overflow-hidden shadow-md fixed"
        :class="{
          'z-[1030]': state.searchInputType === SIDEBAR_SEARCH_INPUT_TYPE,
          'z-[999]': state.searchInputType === PAGE_CONTENT_SEARCH_INPUT_TYPE,
        }"
        :style="partNumberSearchResultsStyle">
        <div
          class="overflow-y-auto"
          :style="{ maxHeight: partNumberSearchResultsStyle.maxHeight }">
          <div
            v-if="state.isPartNumberSearchIndexLoading"
            class="w-full px-4 py-3 flex items-center justify-center gap-2 text-sm text-gray"
            role="status">
            <div class="w-4 h-4 rounded-full border-2 border-current border-t-transparent animate-spin" />
            <span>Loading part numbers...</span>
          </div>
          <div
            v-if="state.partNumberSearchResults.length > 0"
            class="w-full px-4 pt-2 pb-1 flex items-center justify-between gap-2 text-xs font-medium text-light-gray">
            <span>{{ partNumberSearchOptions.length > 0 ? 'Continue search' : 'Search results' }}</span>
            <button
              type="button"
              aria-label="Hide part number suggestions"
              class="border-none bg-transparent p-0 text-xl leading-none text-light-gray hover:text-base-color cursor-pointer tr-all"
              @click="clearPartNumberSearch">
              &times;
            </button>
          </div>
          <a
            v-if="state.partNumberSearchProductUrl.length > 0"
            :href="state.partNumberSearchProductUrl"
            target="_blank"
            rel="noopener noreferrer"
            class="w-full flex items-center justify-between gap-3 px-4 py-3 text-sm font-bold text-base-color bg-gray-100 hover:text-dark-base-color hover:bg-gray-200 cursor-pointer tr-all">
            <span>View product page for all matches</span>
            <svg
              class="flex-none w-4 h-4"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2.4"
              stroke-linecap="round"
              stroke-linejoin="round"
              aria-hidden="true">
              <path d="M5 12h14M13 6l6 6-6 6" />
            </svg>
          </a>
          <button
            v-for="result in partNumberSearchOptions"
            :key="result.part_number"
            type="button"
            class="w-full flex items-center justify-between gap-3 border-none bg-transparent px-4 py-3 text-left text-sm text-gray hover:text-base-color hover:bg-gray-100 cursor-pointer tr-all"
            @click="completePartNumberSearch(result.part_number)">
            <span>
              <span class="font-bold text-black">{{ splitPartNumberByQuery(result.part_number).matched }}</span>{{ splitPartNumberByQuery(result.part_number).rest }}
            </span>
            <svg
              class="flex-none w-4 h-4"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2.2"
              stroke-linecap="round"
              stroke-linejoin="round"
              aria-hidden="true">
              <path d="M17 17L7 7M7 15V7h8" />
            </svg>
          </button>
          <div
            v-if="partNumberSearchOptions.length > 0 && partNumberViewOptions.length > 0"
            class="w-full border-t border-light-gray" />
          <div
            v-if="partNumberViewOptions.length > 0"
            class="w-full px-4 pt-2 pb-1 text-xs font-medium text-light-gray">
            View product page
          </div>
          <a
            v-for="result in partNumberViewOptions"
            :key="`${result.url}-${result.part_number}`"
            :href="result.url"
            target="_blank"
            rel="noopener noreferrer"
            class="w-full flex items-center justify-between gap-3 px-4 py-3 text-sm text-gray hover:text-base-color hover:bg-gray-100 cursor-pointer tr-all">
            <span>
              <span class="font-bold text-black">{{ splitPartNumberByQuery(result.part_number).matched }}</span>{{ splitPartNumberByQuery(result.part_number).rest }}
            </span>
            <svg
              class="flex-none w-4 h-4"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2.2"
              stroke-linecap="round"
              stroke-linejoin="round"
              aria-hidden="true">
              <path d="M14 4h6v6M20 4l-9 9M18 14v5a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V7a1 1 0 0 1 1-1h5" />
            </svg>
          </a>
          <div
            v-if="!state.isPartNumberSearchIndexLoading && state.partNumberSearchResults.length === 0"
            class="w-full p-4 flex flex-col gap-4">
            <div class="flex flex-col gap-1">
              <span class="text-base font-bold leading-tight text-base-color break-words">
                No part number matches “{{ state.searchValue }}”
              </span>
              <span class="text-sm text-gray">
                Check for typos, or enter just the first few characters, like FC-421.
              </span>
            </div>
            <div class="flex flex-col gap-2">
              <button
                type="button"
                class="w-full min-h-[3.5rem] flex items-center justify-between gap-3 rounded-xl border border-solid border-light-gray bg-white px-4 py-2 text-left cursor-pointer hover:border-base-color hover:bg-gray-100 tr-all"
                @click="contactUsHandler">
                <svg
                  class="flex-none w-5 h-5 text-base-color"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  aria-hidden="true">
                  <rect x="3" y="5" width="18" height="14" rx="2" />
                  <path d="M3 7l9 6 9-6" />
                </svg>
                <span class="flex flex-col flex-1">
                  <span class="text-sm font-bold text-base-color">Ask our team</span>
                  <span class="text-xs text-gray">We’ll help you identify the right part</span>
                </span>
                <span
                  class="text-xl leading-none text-gray"
                  aria-hidden="true">&rsaquo;</span>
              </button>
              <button
                type="button"
                class="w-full min-h-[3.5rem] flex items-center justify-between gap-3 rounded-xl border border-solid border-light-gray bg-white px-4 py-2 text-left cursor-pointer hover:border-base-color hover:bg-gray-100 tr-all"
                @click="smartSearchFallbackHandler">
                <svg
                  class="flex-none w-5 h-5 text-base-color"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  aria-hidden="true">
                  <circle cx="12" cy="12" r="9" />
                  <path d="M3 12h18M12 3c3 3 3 15 0 18M12 3c-3 3-3 15 0 18" />
                </svg>
                <span class="flex flex-col flex-1 min-w-0">
                  <span class="text-sm font-bold text-base-color">Search the whole site</span>
                  <span class="text-xs text-gray break-words">Look for “{{ state.searchValue }}” in products and pages</span>
                </span>
                <span
                  class="text-xl leading-none text-gray"
                  aria-hidden="true">&rsaquo;</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </Teleport>

    <div
      v-if="state.isSearchValueErrorVisible"
      class="w-full text-sm text-red">
      Please enter a minimum of
      {{ VariablesHelper.SEARCH_INPUT_MIN_LENGTH }}
      characters.
    </div>
  </div>
</template>

<script setup lang="ts">
// @ts-nocheck

import { computed, nextTick, onBeforeUnmount, onMounted, reactive, ref, watch } from 'vue';
import GlobalPreloaderHelper from '@/helpers/global/globalPreloader.helper';
import VariablesHelper from '@/helpers/global/variables.helper';
import { loadPartNumberSearchIndex } from '@/helpers/website/partNumberSearchIndex.helper';
import type { PartNumberSearchResult } from '@/helpers/website/partNumberSearchIndex.helper';
import TrueFalseLabelsSwitcherComponent from '@/components/website/form-components/TrueFalseLabelsSwitcherComponent.vue';

const SIDEBAR_SEARCH_INPUT_TYPE = 'sidebar-search-input';
const PAGE_CONTENT_SEARCH_INPUT_TYPE = 'page-content-search-input';
const PART_NUMBER_PREFIX_LENGTH = 2;
const PART_NUMBER_DIRECT_RESULTS_LIMIT = 10;
const PART_NUMBER_SEARCH_EMPTY_STATE_DELAY = 1000;
const isPartSearchEnabled = ref(Boolean(IS_PART_SEARCH_ENABLED));

interface PartNumberAutocompleteResult extends PartNumberSearchResult {
  isComplete: boolean;
}

const websiteSearchApp = ref<HTMLElement | null>(null);
const searchInput = ref<HTMLInputElement | null>(null);

const state = reactive({
  selectedCompanyUid: '',
  searchInputType: PAGE_CONTENT_SEARCH_INPUT_TYPE,
  searchValue: '',
  isPartNumberSearch: false,
  isSearchValueErrorVisible: false,
  isPartNumberSearchCompleted: false,
  isPartNumberSearchSubmitted: false,
  isPartNumberSearchIndexLoaded: false,
  isPartNumberSearchIndexLoading: false,
  partNumberSearchIndex: [] as PartNumberSearchResult[],
  partNumberSearchResults: [] as PartNumberAutocompleteResult[],
  partNumberSearchProductUrl: '',
  searchUrl: '/search',
});

const partNumberSearchResultsStyle = reactive({
  top: '0px',
  left: '0px',
  width: '0px',
  maxHeight: '0px',
});

let partNumberSearchPositionContainer: HTMLElement | null = null;
let partNumberSearchPositionAnimationFrame: number | null = null;

const isPartNumberSearchResultsVisible = computed(() => (
  state.isPartNumberSearch
  && (
    state.partNumberSearchResults.length > 0
    || state.isPartNumberSearchIndexLoading
    || (state.isPartNumberSearchSubmitted && state.isPartNumberSearchCompleted)
  )
));

const sidebarSearchInputStyle = computed(() => ({
  borderRadius: isPartNumberSearchResultsVisible.value ? '16px 16px 0 0' : '16px',
}));

const partNumberSearchOptions = computed(() => (
  state.partNumberSearchResults.filter((result) => !result.isComplete)
));

const partNumberViewOptions = computed(() => (
  state.partNumberSearchResults.filter((result) => result.isComplete)
));

const updatePartNumberSearchResultsPosition = () => {
  if (searchInput.value === null) {
    return;
  }

  const inputRect = searchInput.value.getBoundingClientRect();
  const width = Math.min(inputRect.width, window.innerWidth);
  const left = Math.min(Math.max(inputRect.left, 0), window.innerWidth - width);

  partNumberSearchResultsStyle.top = `${inputRect.bottom}px`;
  partNumberSearchResultsStyle.left = `${left}px`;
  partNumberSearchResultsStyle.width = `${width}px`;
  partNumberSearchResultsStyle.maxHeight = `${Math.max(window.innerHeight - inputRect.bottom - 8, 120)}px`;
};

const trackPartNumberSearchResultsPosition = () => {
  updatePartNumberSearchResultsPosition();
  partNumberSearchPositionAnimationFrame = window.requestAnimationFrame(
    trackPartNumberSearchResultsPosition,
  );
};

const startPartNumberSearchPositionTracking = (event: TransitionEvent) => {
  if (
    event.target !== partNumberSearchPositionContainer
    || partNumberSearchPositionAnimationFrame !== null
  ) {
    return;
  }

  trackPartNumberSearchResultsPosition();
};

const stopPartNumberSearchPositionTracking = (event?: TransitionEvent) => {
  if (event !== undefined && event.target !== partNumberSearchPositionContainer) {
    return;
  }

  if (partNumberSearchPositionAnimationFrame !== null) {
    window.cancelAnimationFrame(partNumberSearchPositionAnimationFrame);
    partNumberSearchPositionAnimationFrame = null;
  }

  if (event !== undefined) {
    updatePartNumberSearchResultsPosition();
  }
};

const removePartNumberSearchPositionListeners = () => {
  window.removeEventListener('resize', updatePartNumberSearchResultsPosition);
  window.removeEventListener('scroll', updatePartNumberSearchResultsPosition, true);
  partNumberSearchPositionContainer?.removeEventListener(
    'transitionrun',
    startPartNumberSearchPositionTracking,
  );
  partNumberSearchPositionContainer?.removeEventListener(
    'transitionend',
    stopPartNumberSearchPositionTracking,
  );
  partNumberSearchPositionContainer?.removeEventListener(
    'transitioncancel',
    stopPartNumberSearchPositionTracking,
  );
  stopPartNumberSearchPositionTracking();
  partNumberSearchPositionContainer = null;
};

const updatePartNumberSearchPositionListeners = async (isResultsVisible: boolean) => {
  removePartNumberSearchPositionListeners();
  if (!isResultsVisible) {
    return;
  }

  await nextTick();
  updatePartNumberSearchResultsPosition();
  partNumberSearchPositionContainer = searchInput.value?.closest<HTMLElement>(
    '.website-sidebar, #page-content',
  ) ?? null;
  window.addEventListener('resize', updatePartNumberSearchResultsPosition);
  window.addEventListener('scroll', updatePartNumberSearchResultsPosition, true);
  partNumberSearchPositionContainer?.addEventListener(
    'transitionrun',
    startPartNumberSearchPositionTracking,
  );
  partNumberSearchPositionContainer?.addEventListener(
    'transitionend',
    stopPartNumberSearchPositionTracking,
  );
  partNumberSearchPositionContainer?.addEventListener(
    'transitioncancel',
    stopPartNumberSearchPositionTracking,
  );
};

const getInfoFromDataAttrs = () => {
  if (websiteSearchApp.value !== null) {
    const searchAppContainer = websiteSearchApp.value.parentElement;
    if (searchAppContainer === null) {
      return;
    }

    const dataAttrSearchInputType = searchAppContainer.getAttribute('data-search-input-type');
    if (
      dataAttrSearchInputType === SIDEBAR_SEARCH_INPUT_TYPE
      || dataAttrSearchInputType === PAGE_CONTENT_SEARCH_INPUT_TYPE
    ) {
      state.searchInputType = dataAttrSearchInputType;
    }

    const dataAttrSearchUrl = searchAppContainer.getAttribute('data-search-url');
    if (dataAttrSearchUrl !== null && dataAttrSearchUrl.length > 0) {
      state.searchUrl = dataAttrSearchUrl;
    }

    const dataAttrSearchQ = searchAppContainer.getAttribute('data-search-q');
    if (dataAttrSearchQ !== null && dataAttrSearchQ.length > 0) {
      state.searchValue = decodeURIComponent(dataAttrSearchQ.replace(/\+/g, ' '));
    }

    const dataAttrSearchCompanyUid = searchAppContainer.getAttribute('data-search-company-uid');
    if (dataAttrSearchCompanyUid !== null && dataAttrSearchCompanyUid.length > 0) {
      state.selectedCompanyUid = dataAttrSearchCompanyUid;
    }

    if (searchAppContainer.getAttribute('data-part-search-enabled') === 'false') {
      isPartSearchEnabled.value = false;
      state.isPartNumberSearch = false;
    }
  }
};

const normalizePartNumberSearchQuery = (query: string) => query.toUpperCase().replace(/\s+/g, '');

const splitPartNumberByQuery = (partNumber: string) => {
  const query = normalizePartNumberSearchQuery(state.searchValue);
  const normalizedPartNumber = normalizePartNumberSearchQuery(partNumber);
  let matchedLength = 0;
  while (
    matchedLength < query.length
    && matchedLength < normalizedPartNumber.length
    && query[matchedLength] === normalizedPartNumber[matchedLength]
  ) {
    matchedLength += 1;
  }
  return {
    matched: partNumber.slice(0, matchedLength),
    rest: partNumber.slice(matchedLength),
  };
};

const getCommonPartNumberPrefix = (results: PartNumberSearchResult[]) => {
  let prefix = normalizePartNumberSearchQuery(results[0]?.part_number ?? '');
  for (const result of results) {
    const partNumber = normalizePartNumberSearchQuery(result.part_number);
    while (!partNumber.startsWith(prefix)) {
      prefix = prefix.slice(0, -1);
    }
  }
  return prefix;
};

const searchByCompanyItemsClickHandler = () => {
  const searchByCompanyItems = document.querySelectorAll<HTMLElement>('.search-by-company-item');
  searchByCompanyItems.forEach((item) => {
    item.addEventListener('click', () => {
      const clickedCompanyUid = item.getAttribute('data-company-uid') ?? '';
      state.selectedCompanyUid = state.selectedCompanyUid === clickedCompanyUid
        ? ''
        : clickedCompanyUid;

      searchByCompanyItems.forEach((companyItem) => {
        const isSelected = companyItem.getAttribute('data-company-uid') === state.selectedCompanyUid;
        companyItem.classList.toggle('!text-base-color', isSelected);
        companyItem.classList.toggle('font-medium', isSelected);
      });

      if (state.searchValue.length >= VariablesHelper.SEARCH_INPUT_MIN_LENGTH) {
        searchRequestHandler();
      }
    });
  });
};

let partNumberSearchEmptyStateTimeout: number | null = null;

const clearPartNumberSearchEmptyStateTimeout = () => {
  if (partNumberSearchEmptyStateTimeout !== null) {
    window.clearTimeout(partNumberSearchEmptyStateTimeout);
    partNumberSearchEmptyStateTimeout = null;
  }
};

const clearPartNumberSearch = () => {
  clearPartNumberSearchEmptyStateTimeout();
  state.isPartNumberSearchCompleted = false;
  state.isPartNumberSearchSubmitted = false;
  state.partNumberSearchResults = [];
  state.partNumberSearchProductUrl = '';
};

const updatePartNumberSearchResults = (showEmptyState = false) => {
  let query = normalizePartNumberSearchQuery(state.searchValue);
  if (!state.isPartNumberSearchIndexLoaded) {
    state.isPartNumberSearchCompleted = false;
    state.isPartNumberSearchSubmitted = showEmptyState;
    return;
  }

  state.isPartNumberSearchSubmitted = showEmptyState;
  state.partNumberSearchProductUrl = '';
  let matchingResults = state.partNumberSearchIndex.filter((result) => (
    normalizePartNumberSearchQuery(result.part_number).startsWith(query)
  ));
  while (matchingResults.length === 0 && query.length > 1) {
    query = query.slice(0, -1);
    matchingResults = state.partNumberSearchIndex.filter((result) => (
      normalizePartNumberSearchQuery(result.part_number).startsWith(query)
    ));
  }
  if (matchingResults.length <= PART_NUMBER_DIRECT_RESULTS_LIMIT) {
    state.partNumberSearchResults = matchingResults.map((result) => ({
      ...result,
      isComplete: true,
    }));
    state.isPartNumberSearchCompleted = true;
    return;
  }

  if (matchingResults.every((result) => result.url === matchingResults[0].url)) {
    state.partNumberSearchProductUrl = matchingResults[0].url;
  }

  const nextPrefixLength = Math.max(
    getCommonPartNumberPrefix(matchingResults).length + 1,
    query.length === 0 ? PART_NUMBER_PREFIX_LENGTH : query.length + 1,
  );
  const exactMatches: PartNumberAutocompleteResult[] = [];
  const nextCharacterOptions = new Map<string, PartNumberSearchResult[]>();

  matchingResults.forEach((result) => {
    const normalizedPartNumber = normalizePartNumberSearchQuery(result.part_number);
    if (normalizedPartNumber === query) {
      exactMatches.push({ ...result, isComplete: true });
      return;
    }

    const nextCharacterOption = normalizedPartNumber.slice(0, nextPrefixLength);
    const optionMatches = nextCharacterOptions.get(nextCharacterOption) ?? [];
    optionMatches.push(result);
    nextCharacterOptions.set(nextCharacterOption, optionMatches);
  });

  const nextCharacterResults: PartNumberAutocompleteResult[] = [];
  Array.from(nextCharacterOptions.entries())
    .sort(([firstOption], [secondOption]) => firstOption.localeCompare(secondOption))
    .forEach(([, optionMatches]) => {
      if (optionMatches.length === 1) {
        nextCharacterResults.push({ ...optionMatches[0], isComplete: true });
        return;
      }

      const partNumber = getCommonPartNumberPrefix(optionMatches);
      optionMatches
        .filter((result) => normalizePartNumberSearchQuery(result.part_number) === partNumber)
        .forEach((result) => nextCharacterResults.push({ ...result, isComplete: true }));

      if (optionMatches.some(
        (result) => normalizePartNumberSearchQuery(result.part_number).length > partNumber.length,
      )) {
        nextCharacterResults.push({
          part_number: partNumber,
          url: '',
          isComplete: false,
        });
      }
    });

  state.partNumberSearchResults = [
    ...exactMatches,
    ...nextCharacterResults,
  ];
  state.isPartNumberSearchCompleted = true;
};

const completePartNumberSearch = (partNumber: string) => {
  state.searchValue = partNumber;
  searchInput.value?.focus();
};

const searchPartNumbers = async (isSubmitted = false) => {
  const query = normalizePartNumberSearchQuery(state.searchValue);
  if (!state.isPartNumberSearchIndexLoaded) {
    state.isPartNumberSearchIndexLoading = true;
    try {
      state.partNumberSearchIndex = await loadPartNumberSearchIndex();
    } catch {
      return;
    } finally {
      state.isPartNumberSearchIndexLoading = false;
    }
    state.isPartNumberSearchIndexLoaded = true;
  }

  if (
    !state.isPartNumberSearch
    || query !== normalizePartNumberSearchQuery(state.searchValue)
  ) {
    return;
  }

  updatePartNumberSearchResults(isSubmitted);
  const [singleResult] = state.partNumberSearchResults;
  if (
    isSubmitted
    && state.partNumberSearchResults.length === 1
    && singleResult.isComplete
  ) {
    window.location.href = singleResult.url;
    return;
  }

  if (!isSubmitted) {
    schedulePartNumberSearchEmptyState();
  }
};

const partNumberSearchFocusHandler = () => {
  if (state.isPartNumberSearch && !isPartNumberSearchResultsVisible.value) {
    searchPartNumbers();
  }
};

const schedulePartNumberSearchEmptyState = () => {
  clearPartNumberSearchEmptyStateTimeout();
  if (state.partNumberSearchResults.length > 0) {
    return;
  }

  partNumberSearchEmptyStateTimeout = window.setTimeout(() => {
    if (
      state.isPartNumberSearch
      && state.isPartNumberSearchCompleted
      && state.partNumberSearchResults.length === 0
    ) {
      state.isPartNumberSearchSubmitted = true;
    }
  }, PART_NUMBER_SEARCH_EMPTY_STATE_DELAY);
};

const contactUsHandler = () => {
  const partNumber = normalizePartNumberSearchQuery(state.searchValue);
  if (partNumber.length === 0) {
    return;
  }

  window.dispatchEvent(new CustomEvent(
    VariablesHelper.OPEN_TUTCO_IMMEDIATE_ASSISTANCE_MODAL_EVENT,
    {
      detail: {
        comments: `Hello! I couldn't find any information about part number ${partNumber} on your website. Could you please provide me with more details about this part?`,
      },
    },
  ));
};

const searchInputHandler = () => {
  if (state.isPartNumberSearch) {
    state.searchValue = state.searchValue.toUpperCase();
    state.isSearchValueErrorVisible = false;
    return;
  }

  if (
    state.isSearchValueErrorVisible
    && state.searchValue.trim().length >= VariablesHelper.SEARCH_INPUT_MIN_LENGTH
  ) {
    state.isSearchValueErrorVisible = false;
  }
};

const searchRequestHandler = () => {
  if (state.isPartNumberSearch) {
    clearPartNumberSearch();
    state.isSearchValueErrorVisible = false;
    clearPartNumberSearchEmptyStateTimeout();
    searchPartNumbers(true);
    return;
  }

  if (state.searchValue.trim().length < VariablesHelper.SEARCH_INPUT_MIN_LENGTH) {
    state.isSearchValueErrorVisible = true;
    return;
  }

  GlobalPreloaderHelper.show();
  state.isSearchValueErrorVisible = false;

  const form = document.createElement('form');
  form.method = 'POST';
  form.action = state.searchUrl;

  const csrfField = document.createElement('input');
  csrfField.type = 'hidden';
  csrfField.name = 'csrfmiddlewaretoken';
  csrfField.value = CSRF_TOKEN;
  form.appendChild(csrfField);

  const queryField = document.createElement('input');
  queryField.type = 'hidden';
  queryField.name = 'q';
  queryField.value = `${state.searchValue.replace(' ', '+')}`;
  form.appendChild(queryField);

  if (state.selectedCompanyUid.length > 0) {
    const companyField = document.createElement('input');
    companyField.type = 'hidden';
    companyField.name = 'company';
    companyField.value = state.selectedCompanyUid;
    form.appendChild(companyField);
  }

  document.body.appendChild(form);
  history.replaceState(null, '', window.location.href);
  form.submit();
};

const smartSearchFallbackHandler = async () => {
  state.isPartNumberSearch = false;
  await nextTick();
  searchRequestHandler();
};

const clearSearchInput = () => {
  state.searchValue = '';
  state.isSearchValueErrorVisible = false;
  clearPartNumberSearch();
  searchInput.value?.focus();
};

const clearSidebarSearch = () => {
  if (state.searchInputType !== SIDEBAR_SEARCH_INPUT_TYPE) {
    return;
  }

  state.searchValue = '';
  state.isSearchValueErrorVisible = false;
  clearPartNumberSearch();
};

onMounted(() => {
  state.searchUrl = `${window.location.href.split('search')[0]}search`;
  getInfoFromDataAttrs();
  if (state.searchInputType === PAGE_CONTENT_SEARCH_INPUT_TYPE) {
    searchByCompanyItemsClickHandler();
  }
  window.addEventListener(VariablesHelper.WEBSITE_SIDEBAR_CLOSED_EVENT, clearSidebarSearch);
});

onBeforeUnmount(() => {
  removePartNumberSearchPositionListeners();
  clearPartNumberSearchEmptyStateTimeout();
  window.removeEventListener(VariablesHelper.WEBSITE_SIDEBAR_CLOSED_EVENT, clearSidebarSearch);
});

watch(isPartNumberSearchResultsVisible, updatePartNumberSearchPositionListeners);

watch(
  () => state.searchValue,
  () => {
    if (!state.isPartNumberSearch) {
      return;
    }

    state.isSearchValueErrorVisible = false;
    searchPartNumbers();
  },
);

watch(
  () => state.isPartNumberSearch,
  () => {
    state.isSearchValueErrorVisible = false;
    if (state.isPartNumberSearch) {
      searchPartNumbers();
    } else {
      clearPartNumberSearch();
    }
  },
);
</script>
